"use client";

import React, { useState } from "react";
import { Layers, Gauge, ShieldAlert, ArrowRight, Filter, CircleCheck, FileSpreadsheet, Settings2, Cpu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON, TERMS, type L6 } from "@/lib/commonCopy";
interface LayerSpec {
  position: L6;
  meshCount: string;
  wireDiameter: string;
  aperture: string;
  purpose: L6;
}
interface ProcessApplication {
  resin: L6;
  typicalProcess: L6;
  recommendedPack: string;
  targetFiltration: L6;
}
interface PressureFactor {
  title: L6;
  description: L6;
  impact: L6;
}
const standardPacks: ProcessApplication[] = [{
  resin: {
    en: "LDPE / LLDPE Film",
    zh: "LDPE / LLDPE 吹膜",
    es: "Película de LDPE / LLDPE",
    de: "LDPE / LLDPE-Folie",
    fr: "Film LDPE / LLDPE",
    pt: "Filme de LDPE / LLDPE"
  },
  typicalProcess: {
    en: "Blown film extrusion",
    zh: "吹膜共挤挤出工艺",
    es: "Extrusión de película soplada",
    de: "Blasfolienextrusion",
    fr: "Extrusion de film soufflé",
    pt: "Extrusão de filme tubular"
  },
  recommendedPack: "20 / 60 / 100 / 20 Mesh",
  targetFiltration: {
    en: "140 - 150 Micron",
    zh: "140 - 150 微米",
    es: "140 - 150 micras",
    de: "140 - 150 Mikron",
    fr: "140 - 150 microns",
    pt: "140 - 150 mícrons"
  }
}, {
  resin: {
    en: "HDPE / PP Sheet",
    zh: "HDPE / PP 板材",
    es: "Lámina de HDPE / PP",
    de: "HDPE / PP-Platten",
    fr: "Feuille de HDPE / PP",
    pt: "Chapa de HDPE / PP"
  },
  typicalProcess: {
    en: "Flat die cast sheet",
    zh: "平模头流延/板材挤出",
    es: "Extrusión en matriz plana",
    de: "Breitschlitzdüsen-Plattenextrusion",
    fr: "Extrusion de feuilles en filière plate",
    pt: "Extrusão de chapas em matriz plana"
  },
  recommendedPack: "20 / 40 / 80 / 40 / 20 Mesh",
  targetFiltration: {
    en: "175 - 190 Micron",
    zh: "175 - 190 微米",
    es: "175 - 190 micras",
    de: "175 - 190 Mikron",
    fr: "175 - 190 microns",
    pt: "175 - 190 mícrons"
  }
}, {
  resin: {
    en: "PET / Polyester",
    zh: "PET / 聚酯",
    es: "PET / Poliéster",
    de: "PET / Polyester",
    fr: "PET / Polyester",
    pt: "PET / Poliéster"
  },
  typicalProcess: {
    en: "Bottle preforms & strapping",
    zh: "瓶胚与高强打包带",
    es: "Preformas de botellas y flejes",
    de: "Flaschenvorformlinge & Umreifungsbänder",
    fr: "Préformes de bouteilles et feuillards",
    pt: "Preformas de garrafas e fitas de arqueação"
  },
  recommendedPack: "30 / 100 / 200 / 30 Mesh",
  targetFiltration: {
    en: "70 - 75 Micron",
    zh: "70 - 75 微米",
    es: "70 - 75 micras",
    de: "70 - 75 Mikron",
    fr: "70 - 75 microns",
    pt: "70 - 75 mícrons"
  }
}, {
  resin: {
    en: "Post-Consumer Recycled (PCR)",
    zh: "消费后回收塑料 (PCR)",
    es: "Plástico reciclado posconsumo (PCR)",
    de: "Post-Consumer-Rezyklat (PCR)",
    fr: "Plastique recyclé post-consommation (PCR)",
    pt: "Plástico reciclado pós-consumo (PCR)"
  },
  typicalProcess: {
    en: "Pelletizing & compounding",
    zh: "造粒与改性挤出造粒",
    es: "Pelletización y formulación de compuestos",
    de: "Granulierung & Compoundierung",
    fr: "Granulation et compoundage",
    pt: "Pelotização e formulação de compostos"
  },
  recommendedPack: "20 / 40 / 60 / 80 / 20 Mesh",
  targetFiltration: {
    en: "180 - 250 Micron",
    zh: "180 - 250 微米",
    es: "180 - 250 micras",
    de: "180 - 250 Mikron",
    fr: "180 - 250 microns",
    pt: "180 - 250 mícrons"
  }
}, {
  resin: {
    en: "Engineering Resins (PA/PBT)",
    zh: "工程塑料 (PA/PBT)",
    es: "Resinas de ingeniería (PA/PBT)",
    de: "Technische Kunststoffe (PA/PBT)",
    fr: "Résines techniques (PA/PBT)",
    pt: "Resinas de engenharia (PA/PBT)"
  },
  typicalProcess: {
    en: "Technical profile & compounding",
    zh: "精密型材与工程改性",
    es: "Perfiles técnicos y formulación",
    de: "Technische Profile & Compoundierung",
    fr: "Profilés techniques et compoundage",
    pt: "Perfis técnicos e compostagem"
  },
  recommendedPack: "40 / 120 / 325 / 40 Mesh",
  targetFiltration: {
    en: "40 - 45 Micron",
    zh: "40 - 45 微米",
    es: "40 - 45 micras",
    de: "40 - 45 Mikron",
    fr: "40 - 45 microns",
    pt: "40 - 45 mícrons"
  }
}];
const threeLayerExample: LayerSpec[] = [{
  position: {
    en: "1. Upstream Layer (Melt Entrance)",
    zh: "1. 上游入口层（熔体进入侧）",
    es: "1. Capa aguas arriba (Entrada de fundido)",
    de: "1. Einlasslage schmelzeseitig (Schmelzeeintritt)",
    fr: "1. Couche amont (Entrée du polymère fondu)",
    pt: "1. Camada a montante (Entrada de fundido)"
  },
  meshCount: "20 Mesh",
  wireDiameter: "0.41 mm (0.016 in)",
  aperture: "860 Micron",
  purpose: {
    en: "Captures coarse unmelts and protects the fine filtration wire.",
    zh: "拦截粗大未熔颗粒与团聚物，保护精细核心滤网。",
    es: "Captura partículas no fundidas gruesas y protege la malla fina.",
    de: "Hält grobe Nichtaufschlüsse zurück und schützt das feine Filtergewebe.",
    fr: "Retient les gros infondus et protège la toile fine.",
    pt: "Retém partículas não fundidas grosseiras e protege a tela fina."
  }
}, {
  position: {
    en: "2. Center Control Layer",
    zh: "2. 核心控制层（精密截留）",
    es: "2. Capa central de control",
    de: "2. Zentrale Steuerungslage",
    fr: "2. Couche centrale de contrôle",
    pt: "2. Camada central de controle"
  },
  meshCount: "100 Mesh",
  wireDiameter: "0.10 mm (0.004 in)",
  aperture: "154 Micron",
  purpose: {
    en: "Defines nominal filtration rating and stops carbonized specs.",
    zh: "决定额定过滤精度，阻截微小碳化杂质与胶粒。",
    es: "Define el grado nominal de filtración y retiene motas carbonizadas.",
    de: "Bestimmt die nominale Filterfeinheit und hält verkokte Partikel zurück.",
    fr: "Définit le seuil nominal de filtration et arrête les particules carbonisées.",
    pt: "Define a retenção nominal de filtragem e bloqueia pontos carbonizados."
  }
}, {
  position: {
    en: "3. Downstream Support (Against Breaker Plate)",
    zh: "3. 下游支撑层（贴靠多孔板）",
    es: "3. Soporte aguas abajo (contra placa rompedora)",
    de: "3. Auslassseitige Stützlage (an Lochplatte)",
    fr: "3. Support aval (contre plaque de rupture)",
    pt: "3. Suporte a jusante (contra placa perfurada)"
  },
  meshCount: "20 Mesh",
  wireDiameter: "0.41 mm (0.016 in)",
  aperture: "860 Micron",
  purpose: {
    en: "Prevents the fine control mesh from extruding into breaker plate holes.",
    zh: "防止精细过滤网在熔体高压下挤入多孔板孔眼产生撕裂破损。",
    es: "Evita que la malla fina se extruya hacia los orificios de la placa rompedora.",
    de: "Verhindert, dass das feine Gewebe unter Druck in die Lochplattenbohrungen extrudiert.",
    fr: "Empêche la toile fine de s'extruder dans les orifices de la plaque perforee.",
    pt: "Evita que a tela fina seja empurrada para os furos da placa perfurada."
  }
}];
const fiveLayerExample: LayerSpec[] = [{
  position: {
    en: "1. Upstream Coarse",
    zh: "1. 上游粗滤层",
    es: "1. Filtro grueso aguas arriba",
    de: "1. Schmelzeseitige Groblage",
    fr: "1. Préfiltration amont grossière",
    pt: "1. Filtro grosseiro a montante"
  },
  meshCount: "20 Mesh",
  wireDiameter: "0.41 mm",
  aperture: "860 Micron",
  purpose: {
    en: "Arrests large contaminants before blinding fine screens.",
    zh: "在杂质堵塞精细筛网前预先拦截大颗粒。",
    es: "Retiene contaminantes grandes antes de colmatar las mallas finas.",
    de: "Fängt grobe Partikel ab, bevor sie die Feinsiebe zusetzen.",
    fr: "Retient les impuretés grossières avant colmatage des toiles fines.",
    pt: "Retém contaminantes grandes antes de colmatar as telas finas."
  }
}, {
  position: {
    en: "2. Intermediate Transition",
    zh: "2. 中间过渡层",
    es: "2. Transición intermedia",
    de: "2. Zwischenübergangslage",
    fr: "2. Transition intermédiaire",
    pt: "2. Transição intermediária"
  },
  meshCount: "60 Mesh",
  wireDiameter: "0.19 mm",
  aperture: "233 Micron",
  purpose: {
    en: "Gradual pressure distribution and staged particulate capture.",
    zh: "逐步平衡压力降并实现分级颗粒梯次截留。",
    es: "Distribución gradual de presión y retención escalonada de partículas.",
    de: "Gleichmäßige Druckverteilung und stufenweiser Partikelrückhalt.",
    fr: "Distribution progressive de pression et capture étagée des particules.",
    pt: "Distribuição gradual de pressão e retenção escalonada de partículas."
  }
}, {
  position: {
    en: "3. Primary Filter Core",
    zh: "3. 主控过滤核心层",
    es: "3. Núcleo filtrante principal",
    de: "3. Primäre Filterkernlage",
    fr: "3. Cœur filtrant principal",
    pt: "3. Núcleo filtrante principal"
  },
  meshCount: "150 Mesh",
  wireDiameter: "0.066 mm",
  aperture: "104 Micron",
  purpose: {
    en: "Strict contaminant isolation and optical quality control.",
    zh: "严格隔离细微污染物，保障制品光学纯净度。",
    es: "Aislamiento estricto de contaminantes y control óptico de calidad.",
    de: "Präzise Verunreinigungsabscheidung und optische Qualitätskontrolle.",
    fr: "Isolement strict des contaminants et contrôle de qualité optique.",
    pt: "Isolamento rigoroso de contaminantes e controle de qualidade óptica."
  }
}, {
  position: {
    en: "4. Intermediate Buffer",
    zh: "4. 中间缓冲层",
    es: "4. Amortiguador intermedio",
    de: "4. Zwischenpufferlage",
    fr: "4. Tampon intermédiaire",
    pt: "4. Amortecedor intermediário"
  },
  meshCount: "60 Mesh",
  wireDiameter: "0.19 mm",
  aperture: "233 Micron",
  purpose: {
    en: "Provides cushion between fine cloth and rigid backing.",
    zh: "在精细滤网与高强度支撑网之间提供均匀缓冲。",
    es: "Proporciona amortiguación entre la tela fina y el soporte rígido.",
    de: "Dient als Dämpfungsschicht zwischen Feingewebe und starrer Stützung.",
    fr: "Offre un amorti entre la toile fine et le support rigide.",
    pt: "Proporciona amortecimento entre a tela fina e o suporte rígido."
  }
}, {
  position: {
    en: "5. Downstream Heavy Rigidity",
    zh: "5. 下游刚性重型支撑层",
    es: "5. Soporte rígido pesado aguas abajo",
    de: "5. Auslassseitige starre Schwerlaststützung",
    fr: "5. Support lourd rigide aval",
    pt: "5. Suporte rígido pesado a jusante"
  },
  meshCount: "20 Mesh Heavy",
  wireDiameter: "0.50 mm",
  aperture: "770 Micron",
  purpose: {
    en: "Resists deflection under elevated head pressure differential.",
    zh: "在极端机头高压差下抵抗挠度变形与破网风险。",
    es: "Resiste la flexión bajo elevadas diferencias de presión de cabezal.",
    de: "Widersteht Durchbiegungen bei hohem Kopfdruck-Differenzial.",
    fr: "Résiste à la déformation sous forte différence de pression de filière.",
    pt: "Resiste à deformação sob elevada diferença de pressão no cabeçote."
  }
}];
const pressureFactors: PressureFactor[] = [{
  title: {
    en: "Head pressure & backpressure balance",
    zh: "机头压力与背压平衡机制",
    es: "Equilibrio entre presión de cabezal y contrapresión",
    de: "Kopfdruck- & Gegendruck-Gleichgewicht",
    fr: "Équilibre entre pression de filière et contre-pression",
    pt: "Equilíbrio entre pressão de cabeçote e contrapressão"
  },
  description: {
    en: "A screen pack creates required backpressure to improve resin homogenization and melt temperature uniformity. However, excessive differential pressure causes shear heating, polymer degradation, and output drops.",
    zh: "过滤网组产生必要的背压以提升树脂均化效果与熔体温度均匀性。但过高的压差会导致剪切过热、聚合物热降解及挤出产能下降。",
    es: "El paquete de mallas genera la contrapresión necesaria para homogeneizar la resina y unificar la temperatura. Sin embargo, un diferencial excesivo produce calentamiento por cizalla y degradación.",
    de: "Ein Siebpaket erzeugt den erforderlichen Gegendruck für Schmelzehomogenisierung und gleichmäßige Temperatur. Zu hoher Differenzdruck führt jedoch zu Scherüberhitzung und thermischem Abbau.",
    fr: "Le pack filtrant crée la contre-pression requise pour homogénéiser la résine. Toutefois, une perte de charge excessive entraîne un échauffement par cisaillement et une dégradation du polymère.",
    pt: "O pacote de telas cria a contrapressão necessária para homogeneizar a resina. Contudo, um diferencial excessivo provoca aquecimento por cisalhamento e degradação do polímero."
  },
  impact: {
    en: "Maintain stable differential pressure across the pack assembly",
    zh: "在整个网组组件上保持平稳的运行压差",
    es: "Mantener una presión diferencial estable en todo el conjunto de mallas",
    de: "Stabiler Differenzdruck über das gesamte Siebpaket",
    fr: "Maintien d'une perte de charge stable à travers le pack filtrant",
    pt: "Manter pressão diferencial estável através do conjunto de telas"
  }
}, {
  title: {
    en: "Breaker plate hole span & pack blowout",
    zh: "多孔板孔跨度与滤网冲爆防护",
    es: "Distancia entre orificios de placa y prevención de rotura",
    de: "Lochplattenbohrung & Durchblasschutz",
    fr: "Portée des trous de plaque perforee et risque de perforation",
    pt: "Vão dos furos da placa perfurada e prevenção de rompimento"
  },
  description: {
    en: "Fine wire cloth has low tensile strength under fluid pressure. Without a stiff coarse support wire (typically 20 to 30 mesh) facing the breaker plate, the fine screen extrudes into the holes and tears.",
    zh: "精细丝网在熔体流体压力下的抗拉抗剪强度有限。若贴靠多孔板侧缺乏刚性粗支撑丝网（通常 20-30 目），细网会被挤入孔眼破裂失效。",
    es: "Las telas finas poseen resistencia limitada bajo presión. Sin una malla rígida gruesa (20-30 mesh) contra la placa rompedora, la tela fina se deforma hacia los orificios y se desgarra.",
    de: "Feindrahtgewebe besitzt unter Schmelzedruck begrenzte Zugfestigkeit. Ohne starres Stützgewebe (üblicherweise 20 bis 30 Mesh) zur Lochplatte wird das feine Gewebe eingedrückt und reißt.",
    fr: "La toile fine présente une résistance limitée sous pression. Sans toile de support rigide (20 à 30 mesh) côté plaque, la toile fine est extrudée dans les orifices et se déchire.",
    pt: "A malha fina possui resistência limitada sob pressão de fluido. Sem uma tela grossa de suporte (20 a 30 mesh) voltada para a placa perfurada, a tela fina é prensada nos furos e rompe."
  },
  impact: {
    en: "Support wire diameter should never drop below 0.35 mm",
    zh: "支撑层丝径规格建议不低于 0.35 mm",
    es: "El diámetro del alambre de soporte no debe ser inferior a 0.35 mm",
    de: "Stützdrähte sollten im Durchmesser nicht unter 0,35 mm liegen",
    fr: "Le diamètre du fil de support ne doit pas descendre sous 0,35 mm",
    pt: "O diâmetro do arame de suporte nunca deve ser inferior a 0,35 mm"
  }
}, {
  title: {
    en: "Contaminant cake buildup rate",
    zh: "滤饼形成速率与在线运行寿命",
    es: "Tasa de formación de torta y vida útil en línea",
    de: "Filterkuchen-Aufbaurate & Standzeit",
    fr: "Vitesse de formation du gâteau et durée de vie en service",
    pt: "Taxa de formação de torta e vida útil em operação"
  },
  description: {
    en: "As particulate builds on the upstream screen, open area decreases logarithmically. Selecting a layered pack with progressive aperture gradient extends online life by capturing debris across depths.",
    zh: "随着杂质在上游筛网表面累积，有效开孔面积呈对数衰减。选用具备孔径梯度的多层网组可实现深度分级截留，大幅延长在线寿命。",
    es: "A medida que se acumulan partículas, el área abierta disminuye exponencialmente. Un paquete con gradiente progresivo de aperturas prolonga la vida útil al retener residuos en profundidad.",
    de: "Mit zunehmender Partikelablagerung sinkt die offene Siebfläche logarithmisch. Ein abgestuftes Mehrlagenpaket fängt Verunreinigungen in der Tiefe ab und verlängert die Standzeit.",
    fr: "À mesure que les particules s'accumulent, la surface ouverte diminue fortement. Un pack à gradient d'ouverture progressif prolonge la durée de service en retenant les impuretés en profondeur.",
    pt: "À medida que as partículas se acumulam, a área aberta diminui rapidamente. Um pacote multicamadas com gradiente progressivo estende a vida útil retendo detritos em profundidade."
  },
  impact: {
    en: "Multi-layer packs offer up to 3x longer on-stream life than single meshes",
    zh: "多层梯度网组相比单层滤网可提供高达 3 倍的在线连续运行寿命",
    es: "Los paquetes multicapa ofrecen hasta 3 veces mayor vida útil que una sola malla",
    de: "Mehrlagenpakete bieten bis zu 3-mal längere Standzeiten als Einzellagen",
    fr: "Les packs multicouches offrent jusqu'à 3 fois plus de longévité qu'une toile unique",
    pt: "Pacotes multicamadas oferecem vida útil até 3 vezes maior do que telas simples"
  }
}];
const guideCopy = {
  sectionBadge: {
    en: "Extrusion Engineering Reference",
    zh: "挤出工程技术参考",
    es: "Referencia de ingeniería de extrusión",
    de: "Extrusions-Engineering-Referenz",
    fr: "Référence d'ingénierie d'extrusion",
    pt: "Referência de engenharia de extrusão"
  },
  h2: {
    en: "Extrusion screen pack guide",
    zh: "挤出过滤网组选型与配置指南",
    es: "Guía de paquetes de mallas para extrusión",
    de: "Leitfaden für Extrusionssiebpakete",
    fr: "Guide des packs de toiles d'extrusion",
    pt: "Guia de pacotes de telas para extrusão"
  },
  lead: {
    en: "Select the right mesh counts, layer structures, and wire diameters to balance melt cleanliness with stable head pressure across continuous plastics and rubber extrusion lines.",
    zh: "精确匹配目数规格、层级结构与丝径参数，在连续塑料与橡胶挤出生产线中实现熔体纯净度与稳定机头压力的平衡。",
    es: "Seleccione el número de mallas, estructura de capas y diámetros de alambre adecuados para equilibrar la pureza del fundido con una presión estable en líneas de plástico y caucho.",
    de: "Wählen Sie Maschenzahlen, Lagenaufbau und Drahtdurchmesser präzise aus, um Schmelzereinheit und stabilen Kopfdruck bei der kontinuierlichen Kunststoff- und Kautschukextrusion auszubalancieren.",
    fr: "Sélectionnez les nombres de mailles, structures de couches et diamètres de fil pour équilibrer propreté du polymère et pression stable sur vos lignes d'extrusion plastique et caoutchouc.",
    pt: "Selecione as contagens de malha, estrutura de camadas e diâmetros de arame corretos para equilibrar a limpeza do fundido com pressão estável em linhas de plásticos e borracha."
  },
  pillar1Title: {
    en: "1. Mesh count & aperture",
    zh: "1. 目数与开孔精度",
    es: "1. Número de mallas y abertura",
    de: "1. Maschenzahl & Maschenweite",
    fr: "1. Nombre de mailles et ouverture",
    pt: "1. Contagem de malha e abertura"
  },
  pillar1Desc: {
    en: "Particle retention rating",
    zh: "杂质微粒截留等级",
    es: "Nivel de retención de partículas",
    de: "Partikelrückhaltegrad",
    fr: "Seuil de rétention des particules",
    pt: "Classificação de retenção de partículas"
  },
  pillar1Body: {
    en: "Target mesh count depends on downstream tolerances. Film and optical sheet demand 100 to 325 mesh (about 40-150 micron), while structural profiles and pipe run 20 to 60 mesh.",
    zh: "目标目数取决于下游制品精度公差。吹膜与光学级板材要求 100 至 325 目（约 40-150 微米），而结构型材与管材通常采用 20 至 60 目。",
    es: "El número de malla depende de las tolerancias requeridas. Película y lámina óptica demandan de 100 a 325 mesh (40-150 micras), mientras que perfiles y tuberías emplean de 20 a 60 mesh.",
    de: "Die Zielmaschenzahl hängt von den Toleranzen ab. Folien und optische Platten erfordern 100 bis 325 Mesh (ca. 40-150 Mikron), Strukturprofile und Rohre 20 bis 60 Mesh.",
    fr: "Le nombre de mailles dépend des tolérances requises. Les films et plaques optiques exigent 100 à 325 mesh (40-150 microns), tandis que profilés et tubes utilisent 20 à 60 mesh.",
    pt: "A contagem de malha depende das tolerâncias exigidas. Filmes e chapas ópticas demandam de 100 a 325 mesh (40-150 mícrons), enquanto perfis e tubos usam 20 a 60 mesh."
  },
  pillar1Badge: {
    en: "AISI 304 / 316L Stainless Steel",
    zh: "AISI 304 / 316L 不锈钢",
    es: "Acero inoxidable AISI 304 / 316L",
    de: "AISI 304 / 316L Edelstahl",
    fr: "Acier inoxydable AISI 304 / 316L",
    pt: "Aço inoxidável AISI 304 / 316L"
  },
  pillar2Title: {
    en: "2. Layer arrangement",
    zh: "2. 多层结构排布",
    es: "2. Disposición de capas",
    de: "2. Lagenanordnung",
    fr: "2. Agencement des couches",
    pt: "2. Disposição das camadas"
  },
  pillar2Desc: {
    en: "Symmetrical & progressive packs",
    zh: "对称型与渐进式组合",
    es: "Paquetes simétricos y progresivos",
    de: "Symmetrische & progressive Pakete",
    fr: "Packs symétriques et progressifs",
    pt: "Pacotes simétricos e progressivos"
  },
  pillar2Body: {
    en: "Always sandwich fragile fine mesh between coarse support layers. Spot-welded or aluminum-rimmed packs ensure workers install the assembly in the exact orientation required.",
    zh: "高精细滤网需置于粗支撑层之间进行保护。点焊或铝/铜边包边组合网组可确保操作人员在快速换网时保持精准的装配方向。",
    es: "Proteja siempre la malla fina entre capas gruesas de soporte. Los paquetes soldados por puntos o con marco de aluminio garantizan una orientación de montaje exacta.",
    de: "Feine Gewebelagen müssen stets zwischen groben Stützlagen eingebettet werden. Punktgeschweißte oder eingefasste Pakete sichern die korrekte Einbaurichtung.",
    fr: "Protégez toujours la toile fine entre deux couches de support grossier. Les packs soudés par points ou bordés d'aluminium garantissent l'orientation exacte au montage.",
    pt: "Sempre monte a malha fina entre camadas grossas de suporte. Pacotes ponteados ou com aro de alumínio asseguram a orientação correta de montagem."
  },
  pillar2Badge: {
    en: "Spot-Welded or Framed Discs",
    zh: "点焊或金属包边滤片",
    es: "Discos soldados o con marco metálico",
    de: "Punktgeschweißte oder gerahmte Ronden",
    fr: "Disques soudés ou bordés",
    pt: "Discos ponteados ou emoldurados"
  },
  pillar3Title: {
    en: "3. Pressure considerations",
    zh: "3. 熔体压力与刚性考量",
    es: "3. Consideraciones de presión",
    de: "3. Druck- und Steifigkeitskriterien",
    fr: "3. Considérations de pression",
    pt: "3. Critérios de pressão"
  },
  pillar3Desc: {
    en: "Delta P & structural rigidity",
    zh: "压差 ΔP 与结构抗剪切",
    es: "Delta P y rigidez estructural",
    de: "Differenzdruck ΔP & Struktursteifigkeit",
    fr: "Perte de charge ΔP et rigidité structurelle",
    pt: "Delta P e rigidez estrutural"
  },
  pillar3Body: {
    en: "Higher mesh counts cause rapid differential pressure spikes. Select wire diameters with sufficient open area percentage to prevent melt temperature rise and screen blowout.",
    zh: "高目数细网会导致压差迅速攀升。需选择具备充足开孔率的丝径规格，防止熔体温升剪切降解及滤网冲爆风险。",
    es: "Mallas más finas generan picos rápidos de presión diferencial. Seleccione diámetros de alambre con suficiente área abierta para evitar calentamiento y roturas.",
    de: "Höhere Maschenzahlen bewirken rasche Druckanstiege. Wählen Sie Drahtdurchmesser mit ausreichender freier Fläche, um Schmelzeüberhitzung und Siebbruch zu vermeiden.",
    fr: "Des maillages plus fins provoquent des montées rapides de perte de charge. Choisissez des fils avec surface ouverte suffisante pour éviter échauffement et rupture.",
    pt: "Contagens mais finas de malha provocam picos rápidos de pressão. Escolha diâmetros de arame com área aberta adequada para evitar superaquecimento e ruptura."
  },
  pillar3Badge: {
    en: "Differential pressure control",
    zh: "压差控制",
    es: "Control de ΔP",
    de: "ΔP-Kontrolle",
    fr: "Contrôle du ΔP",
    pt: "Controle de ΔP"
  },
  tabCardTitle: {
    en: "Screen pack layer configurations",
    zh: "过滤网组层级配置架构",
    es: "Configuraciones de capas del paquete filtrante",
    de: "Lagenaufbau von Siebpaketen",
    fr: "Configurations des couches de packs filtrants",
    pt: "Configurações de camadas de pacotes filtrantes"
  },
  tabCardDesc: {
    en: "Compare cross-section layer sequence for standard vs heavy-duty melt filtration.",
    zh: "对比标准熔体过滤与重载高杂质工况下的截面层级排布序列。",
    es: "Compare la secuencia de capas en sección transversal para filtración estándar frente a servicio pesado.",
    de: "Vergleichen Sie den Querschnittsaufbau für Standard- und Schwerlast-Schmelzefiltration.",
    fr: "Comparez l'agencement des couches en coupe pour filtration standard et service intensif.",
    pt: "Compare a sequência de camadas em corte para filtração padrão versus serviço pesado."
  },
  tab3LayerTrigger: {
    en: "3-Layer Standard Pack",
    zh: "3 层标准对称网组",
    es: "Paquete estándar de 3 capas",
    de: "3-lagiges Standardpaket",
    fr: "Pack standard 3 couches",
    pt: "Pacote padrão de 3 camadas"
  },
  tab5LayerTrigger: {
    en: "5-Layer Progressive Pack",
    zh: "5 层梯次深度网组",
    es: "Paquete progresivo de 5 capas",
    de: "5-lagiges progressives Paket",
    fr: "Pack progressif 5 couches",
    pt: "Pacote progressivo de 5 camadas"
  },
  crossSectionTitle3: {
    en: "Cross-Section Sequence",
    zh: "截面排布序列",
    es: "Secuencia de sección transversal",
    de: "Querschnittsfolge",
    fr: "Séquence en coupe",
    pt: "Sequência de corte"
  },
  crossSectionInflow: {
    en: "Melt Inflow Side:",
    zh: "熔体流入侧：",
    es: "Lado de entrada de fundido:",
    de: "Schmelzeeintrittsseite:",
    fr: "Côté entrée du fondu :",
    pt: "Lado de entrada do fundido:"
  },
  crossSectionCore: {
    en: "Filtration Core:",
    zh: "核心过滤层：",
    es: "Núcleo de filtración:",
    de: "Filterkern:",
    fr: "Cœur de filtration :",
    pt: "Núcleo de filtração:"
  },
  crossSectionCoreFine100: {
    en: "100 Mesh Fine",
    zh: "100 目精细网",
    es: "100 Mesh Fina",
    de: "100 Mesh Feingewebe",
    fr: "Toile fine 100 Mesh",
    pt: "Tela fina 100 Mesh"
  },
  crossSectionSupport: {
    en: "Breaker Plate Support:",
    zh: "多孔板支撑侧：",
    es: "Soporte de placa rompedora:",
    de: "Lochplatten-Stützseite:",
    fr: "Support côté plaque perforée :",
    pt: "Suporte da placa perfurada:"
  },
  crossSectionFootnote3: {
    en: "Symmetrical sandwich prevents inverted installation error on production floor.",
    zh: "对称式三明治结构，彻底杜绝车间现场反向安装错误。",
    es: "Estructura sándwich simétrica que elimina errores de orientación en planta.",
    de: "Symmetrischer Sandwich-Aufbau verhindert fehlerhafte Montage vor Ort.",
    fr: "La structure sandwich symétrique évite toute erreur de sens lors du montage en atelier.",
    pt: "Estrutura sanduíche simétrica que impede montagem invertida na produção."
  },
  crossSectionTitle5: {
    en: "Progressive Depth Filtering",
    zh: "渐进式深度过滤",
    es: "Filtración progresiva en profundidad",
    de: "Progressive Tiefenfiltration",
    fr: "Filtration progressive en profondeur",
    pt: "Filtração progressiva em profundidade"
  },
  stepTopGuard: {
    en: "1. Top Guard:",
    zh: "1. 顶部保护层：",
    es: "1. Protección superior:",
    de: "1. Oberer Schutz:",
    fr: "1. Protection supérieure :",
    pt: "1. Proteção superior:"
  },
  stepCoarseStep: {
    en: "2. Coarse Step:",
    zh: "2. 粗滤过渡层：",
    es: "2. Etapa gruesa:",
    de: "2. Grobstufe:",
    fr: "2. Étape grossière :",
    pt: "2. Etapa grossa:"
  },
  stepPrecisionCore: {
    en: "3. Precision Core:",
    zh: "3. 精密核心层：",
    es: "3. Núcleo de precisión:",
    de: "3. Präzisionskern:",
    fr: "3. Cœur de précision :",
    pt: "3. Núcleo de precisão:"
  },
  stepCushionBed: {
    en: "4. Cushion Bed:",
    zh: "4. 缓冲过渡层：",
    es: "4. Lecho amortiguador:",
    de: "4. Pufferlage:",
    fr: "4. Couche tampon :",
    pt: "4. Leito amortecedor:"
  },
  stepRigidBase: {
    en: "5. Rigid Base:",
    zh: "5. 刚性基底层：",
    es: "5. Base rígida:",
    de: "5. Starre Basis:",
    fr: "5. Base rigide :",
    pt: "5. Base rígida:"
  },
  crossSectionFootnote5: {
    en: "Designed for high-viscosity resins and recycled feedstocks with wide dirt distribution.",
    zh: "适用于高粘度聚合物及杂质粒径分布较宽的回收再生料工况。",
    es: "Diseñado para resinas de alta viscosidad y reciclados con amplia distribución de impurezas.",
    de: "Ausgelegt für hochviskose Polymere und Rezyklate mit breiter Schmutzpartikelverteilung.",
    fr: "Conçu pour résines à haute viscosité et charges recyclées à large spectre d'impuretés.",
    pt: "Projetado para resinas de alta viscosidade e reciclados com ampla distribuição de detritos."
  },
  thLayerPosition: {
    en: "Layer Position",
    zh: "层级位置",
    es: "Posición de capa",
    de: "Lagenposition",
    fr: "Position de la couche",
    pt: "Posição da camada"
  },
  thMeshSpec: {
    en: "Mesh Specification",
    zh: "目数规格",
    es: "Especificación de malla",
    de: "Maschenspezifikation",
    fr: "Spécification de maille",
    pt: "Especificação de malha"
  },
  thWireAperture: {
    en: "Wire / Aperture",
    zh: "丝径 / 开孔尺寸",
    es: "Alambre / Abertura",
    de: "Draht / Maschenweite",
    fr: "Fil / Ouverture",
    pt: "Arame / Abertura"
  },
  thFunction: {
    en: "Function",
    zh: "功能作用",
    es: "Función",
    de: "Funktion",
    fr: "Fonction",
    pt: "Função"
  },
  matrixTitle: {
    en: "Screen pack selection matrix by resin & process",
    zh: "按树脂种类与工艺划分的滤网选型矩阵",
    es: "Matriz de selección de paquetes por resina y proceso",
    de: "Auswahlmatrix für Siebpakete nach Polymer & Verfahren",
    fr: "Matrice de sélection des packs selon la résine et le procédé",
    pt: "Matriz de seleção de pacotes por resina e processo"
  },
  matrixDesc: {
    en: "Typical configurations used in industrial extrusion facilities worldwide.",
    zh: "全球工业挤出生产设施中常用的典型推荐配置。",
    es: "Configuraciones típicas utilizadas en plantas industriales de extrusión en todo el mundo.",
    de: "Typische Konfigurationen industrieller Extrusionsanlagen weltweit.",
    fr: "Configurations types employées sur les lignes industrielles d'extrusion mondiales.",
    pt: "Configurações típicas utilizadas em plantas industriais de extrusão em todo o mundo."
  },
  viewAllSpecs: {
    en: "View All Screen Pack Specs",
    zh: "查看全部滤网规格",
    es: "Ver todas las especificaciones de paquetes",
    de: "Alle Siebpaket-Spezifikationen ansehen",
    fr: "Voir toutes les spécifications de packs",
    pt: "Ver todas as especificações de pacotes"
  },
  thResinGrade: {
    en: "Polymer & Grade",
    zh: "聚合物与牌号",
    es: "Polímero y calidad",
    de: "Polymer & Güte",
    fr: "Polymère et nuance",
    pt: "Polímero e grau"
  },
  thProcess: {
    en: "Extrusion Process",
    zh: "挤出工艺",
    es: "Proceso de extrusión",
    de: "Extrusionsverfahren",
    fr: "Procédé d'extrusion",
    pt: "Processo de extrusão"
  },
  thRecommendedPack: {
    en: "Recommended Mesh Pack",
    zh: "推荐滤网组合",
    es: "Paquete de mallas recomendado",
    de: "Empfohlenes Siebpaket",
    fr: "Pack de toiles recommandé",
    pt: "Pacote de telas recomendado"
  },
  thApertureRating: {
    en: "Filtration Aperture",
    zh: "过滤精度",
    es: "Abertura de filtración",
    de: "Filterfeinheit",
    fr: "Seuil de filtration",
    pt: "Abertura de filtração"
  },
  pressureDynamicsTitle: {
    en: "Pressure dynamics & screen pack failure prevention",
    zh: "熔体压力动态与滤网破损预防",
    es: "Dinámica de presión y prevención de rotura de mallas",
    de: "Druckdynamik & Vermeidung von Siebschäden",
    fr: "Dynamique de pression et prévention des ruptures de packs",
    pt: "Dinâmica de pressão e prevenção de ruptura de telas"
  },
  pressureDynamicsDesc: {
    en: "Melt pressure management is critical to protecting screw drives, avoiding barrel leakage, and maintaining gauge uniformity.",
    zh: "严格的熔体压力管理对于保护螺杆驱动、杜绝料筒缝隙漏胶以及维持制品厚度均匀性至关重要。",
    es: "El control de presión es esencial para proteger el husillo, evitar fugas en el cilindro y mantener espesores uniformes.",
    de: "Schmelzedrucküberwachung schützt Schneckenantriebe, vermeidet Leckagen am Zylinder und sichert gleichmäßige Wandstärken.",
    fr: "La gestion de pression est essentielle pour protéger l'entraînement de la vis, éviter les fuites et préserver la régularité d'épaisseur.",
    pt: "O controle de pressão é crítico para proteger a rosca, evitar vazamentos no canhão e manter espessuras uniformes."
  },
  alertTitle: {
    en: "Engineering Alert: Screen Breakage Risks",
    zh: "工程警示：滤网破损风险",
    es: "Alerta técnica: Riesgos de rotura de mallas",
    de: "Technischer Warnhinweis: Risiko von Siebbruch",
    fr: "Alerte ingénierie : Risque de rupture de toile",
    pt: "Alerta de engenharia: Riscos de ruptura de tela"
  },
  alertP1: {
    en: "Never run single-disc fine mesh (100+ mesh) directly against a breaker plate without downstream coarse reinforcement. Fluid shear pushes the fine wires into the holes, causing wire shearing and un-filtered contaminants in the die.",
    zh: "切勿在缺乏下游粗网支撑的情况下，将单层高目数细网（100 目以上）直接贴靠多孔板运行。流体剪切力会将细丝压入孔眼导致切断撕裂，使未过滤杂质直接进入模头。",
    es: "Nunca trabaje con una sola malla fina (100+ mesh) directamente sobre la placa rompedora sin refuerzo grueso posterior. El cizallamiento arrastra los alambres a los orificios, desgarrándolos y permitiendo impurezas en la matriz.",
    de: "Betreiben Sie niemals eine einzelne Feindrahtlage (100+ Mesh) ohne nachgeschaltete grobe Stützung direkt an der Lochplatte. Die Scherkraft drückt feine Drähte in die Bohrungen und führt zum Riss.",
    fr: "Ne faites jamais fonctionner une toile fine unique (100+ mesh) directement contre la plaque perforee sans renfort amont/aval grossier. Les forces de cisaillement coupent les fils fins dans les trous.",
    pt: "Nunca utilize uma única tela fina (100+ mesh) diretamente sobre a placa perfurada sem suporte grosso a jusante. O cisalhamento força os fios para dentro dos furos, causando rompimento."
  },
  alertP2: {
    en: "For continuous screen changers, verify edge spot-weld integrity and rim sealing to prevent molten polymer bypassing around perimeter gaps.",
    zh: "对于连续换网器，请务必核查边缘点焊牢固度与包边密封性，防止熔融聚合物从边缘间隙短路旁漏。",
    es: "En cambiadores continuos de mallas, compruebe la solidez de los puntos de soldadura y el sellado perimetral para evitar fugas de polímero fundido.",
    de: "Prüfen Sie bei kontinuierlichen Siebwechslern die Randschweißpunkte und Einfassungsdichtigkeit, um Schmelzebypässe an den Kanten auszuschließen.",
    fr: "Pour changeurs continus de filtres, vérifiez la tenue des soudures périphériques et le sertissage pour éliminer tout contournement du polymère.",
    pt: "Em trocadores contínuos de tela, verifique a solda periférica e a vedação da borda para impedir desvios do polímero fundido pelas frestas."
  },
  packTypesCardTitle: {
    en: "Spot-Welded vs Framed Packs",
    zh: "点焊滤片 vs 金属包边网组",
    es: "Discos soldados por puntos vs paquetes con marco",
    de: "Punktgeschweißt vs. Gerahmte Pakete",
    fr: "Packs soudés par points vs packs bordés",
    pt: "Discos ponteados vs pacotes emoldurados"
  },
  packType1Title: {
    en: "Spot-Welded Packs:",
    zh: "点焊组合网片：",
    es: "Paquetes soldados por puntos:",
    de: "Punktgeschweißte Pakete:",
    fr: "Packs soudés par points :",
    pt: "Pacotes ponteados:"
  },
  packType1Desc: {
    en: "Economical, securely bonded perimeter points ensure correct stacking sequence during rapid manual changes.",
    zh: "经济高效，牢固的边缘点焊确保在人工快速换网时各层排列顺序不发生错位。",
    es: "Económicos y con puntos perimetrales sólidos que aseguran la secuencia correcta durante cambios manuales rápidos.",
    de: "Wirtschaftlich und mit robusten Randpunkten, die bei schnellen Handwechseln die richtige Reihenfolge sichern.",
    fr: "Économiques, avec points périphériques résistants maintenant l'ordre exact lors des changements manuels rapides.",
    pt: "Econômicos e com pontos periféricos firmes que mantêm o empilhamento correto nas trocas manuais rápidas."
  },
  packType2Title: {
    en: "Aluminum/Copper Framed Packs:",
    zh: "铝边/铜边包边网组：",
    es: "Paquetes con marco de aluminio/cobre:",
    de: "Aluminium-/Kupfer-gerahmte Pakete:",
    fr: "Packs bordés aluminium ou cuivre :",
    pt: "Pacotes com borda de alumínio/cobre:"
  },
  packType2Desc: {
    en: "Crimped metal border acts as a high-pressure gasket, eliminating polymer edge leakage in high-viscosity lines.",
    zh: "压接金属包边兼具高压垫片密封功能，在高粘度挤出线中彻底消除边缘漏料与跑胶。",
    es: "El borde metálico engarzado actúa como junta de alta presión, evitando fugas perimetrales en líneas de alta viscosidad.",
    de: "Die gebördelte Metalleinfassung dichtet als Hochdruckdichtung ab und eliminiert Schmelzeaustritt an den Rändern.",
    fr: "La bordure sertie fait office de joint haute pression, éliminant toute fuite périphérique sur produits visqueux.",
    pt: "A borda metálica cravada atua como junta de alta pressão, eliminando vazamentos de borda em resinas viscosas."
  },
  packType3Title: {
    en: "Sintered Multilayer Discs:",
    zh: "多层烧结滤片：",
    es: "Discos sinterizados multicapa:",
    de: "Mehrlagige Sinterronden:",
    fr: "Disques multicouches frittés :",
    pt: "Discos sinterizados multicamadas:"
  },
  packType3Desc: {
    en: "Diffusion-bonded solid composite for automated continuous belt screen changers.",
    zh: "固态扩散焊接复合结构，适用于高压自动反冲洗及连续带式换网器。",
    es: "Compuesto sólido soldado por difusión para cambiadores continuos automáticos de cinta.",
    de: "Diffusionsverschweißter Vollverbund für automatische kontinuierliche Bandsiebwechsler.",
    fr: "Composite rigide soudé par diffusion pour changeurs continus automatiques à bande.",
    pt: "Compósito sólido soldado por difusão para trocadores de tela contínuos de fita automática."
  },
  ctaBoxTitle: {
    en: "Need custom diameter discs or tailored mesh combinations?",
    zh: "需要定制尺寸滤片或特殊目数层级组合？",
    es: "¿Necesita discos de diámetro a medida o combinaciones especiales?",
    de: "Benötigen Sie Sonderdurchmesser oder maßgeschneiderte Lagenkombinationen?",
    fr: "Besoin de disques sur mesure ou de combinaisons de maillages personnalisées ?",
    pt: "Precisa de discos com diâmetros sob medida ou combinações personalizadas?"
  },
  ctaBoxDesc: {
    en: "MutualFilter produces stamped screen packs, circular discs, kidney shapes, and cylinder screens in customized stainless steel 304, 316, and 316L wire alloys.",
    zh: "MutualFilter 生产冲压滤网组、圆形滤片、腰形滤网以及圆筒滤网，支持 304、316 及 316L 不锈钢定制。",
    es: "MutualFilter fabrica paquetes troquelados, discos circulares, formas arriñonadas y filtros cilíndricos en aleaciones de acero inoxidable 304, 316 y 316L.",
    de: "MutualFilter fertigt gestanzte Siebpakete, Ronden, Nierenformen und Filterzylinder aus Edelstahl 304, 316 und 316L.",
    fr: "MutualFilter produit des packs estampés, disques circulaires, formes haricot et toiles cylindriques en inox 304, 316 et 316L.",
    pt: "A MutualFilter produz pacotes estampados, discos circulares, formatos rim e telas cilíndricas em aço inoxidável 304, 316 e 316L."
  },
  requestCustomQuote: {
    en: "Request Custom Pack Quote",
    zh: "索取定制网组报价",
    es: "Solicitar cotización de paquete a medida",
    de: "Sonderpaket-Angebot anfragen",
    fr: "Demander un devis pour packs sur mesure",
    pt: "Solicitar orçamento de pacote sob medida"
  },
  technicalLibrary: {
    en: "Technical Library",
    zh: "技术文献库",
    es: "Biblioteca técnica",
    de: "Technische Bibliothek",
    fr: "Bibliothèque technique",
    pt: "Biblioteca técnica"
  }
};
export default function ExtrusionGuide() {
  const {
    lang
  } = useLanguage();
  const [activePackType, setActivePackType] = useState<string>("3-layer");
  return <section id="extrusionscreenpackguide" data-nav="light" className="bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container">
        {/* Section Header */}
        <Reveal>
          <div className="max-w-3xl mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md border border-border bg-card text-foreground mf-caption font-mono uppercase tracking-wider mb-4">
              <Layers className="size-3.5 text-primary" aria-hidden="true" />
              <span>{pick(lang, guideCopy.sectionBadge)}</span>
            </div>
            <h2 className="mf-h2 text-foreground font-default">
              {pick(lang, guideCopy.h2)}
            </h2>
            <p className="mt-4 mf-lead text-muted-foreground">
              {pick(lang, guideCopy.lead)}
            </p>
          </div>
        </Reveal>

        {/* 3 Core Selection Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <Reveal delay={0}>
            <Card className="h-full border-border bg-card rounded-lg flex flex-col justify-between">
              <CardHeader className="pb-3">
                <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary mb-3">
                  <Filter className="size-5" aria-hidden="true" />
                </div>
                <CardTitle className="mf-h3 text-card-foreground">
                  {pick(lang, guideCopy.pillar1Title)}
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  {pick(lang, guideCopy.pillar1Desc)}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-foreground/90 space-y-2">
                <p>{pick(lang, guideCopy.pillar1Body)}</p>
                <div className="pt-2">
                  <Badge variant="outline" className="mf-caption font-mono">
                    {pick(lang, guideCopy.pillar1Badge)}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </Reveal>

          <Reveal delay={60}>
            <Card className="h-full border-border bg-card rounded-lg flex flex-col justify-between">
              <CardHeader className="pb-3">
                <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary mb-3">
                  <Layers className="size-5" aria-hidden="true" />
                </div>
                <CardTitle className="mf-h3 text-card-foreground">
                  {pick(lang, guideCopy.pillar2Title)}
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  {pick(lang, guideCopy.pillar2Desc)}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-foreground/90 space-y-2">
                <p>{pick(lang, guideCopy.pillar2Body)}</p>
                <div className="pt-2">
                  <Badge variant="outline" className="mf-caption font-mono">
                    {pick(lang, guideCopy.pillar2Badge)}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </Reveal>

          <Reveal delay={120}>
            <Card className="h-full border-border bg-card rounded-lg flex flex-col justify-between">
              <CardHeader className="pb-3">
                <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary mb-3">
                  <Gauge className="size-5" aria-hidden="true" />
                </div>
                <CardTitle className="mf-h3 text-card-foreground">
                  {pick(lang, guideCopy.pillar3Title)}
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  {pick(lang, guideCopy.pillar3Desc)}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-foreground/90 space-y-2">
                <p>{pick(lang, guideCopy.pillar3Body)}</p>
                <div className="pt-2">
                  <Badge variant="outline" className="mf-caption font-mono">
                    {pick(lang, guideCopy.pillar3Badge)}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Detailed Layer Architecture Explorer */}
        <Reveal delay={120}>
          <div className="mb-16">
            <div className="border border-border rounded-lg bg-card p-6 sm:p-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                <div>
                  <h3 className="mf-h3 text-card-foreground">
                    {pick(lang, guideCopy.tabCardTitle)}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {pick(lang, guideCopy.tabCardDesc)}
                  </p>
                </div>

                <Tabs value={activePackType} onValueChange={setActivePackType} className="w-full sm:w-auto">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="3-layer" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                      {pick(lang, guideCopy.tab3LayerTrigger)}
                    </TabsTrigger>
                    <TabsTrigger value="5-layer" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                      {pick(lang, guideCopy.tab5LayerTrigger)}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {activePackType === "3-layer" && <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                    <div className="lg:col-span-4 bg-muted/40 p-5 rounded-lg border border-border flex flex-col gap-3">
                      <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                        <Cpu className="size-4" />
                        <span>{pick(lang, guideCopy.crossSectionTitle3)}</span>
                      </div>
                      <div className="space-y-2">
                        <div className="p-3 bg-muted text-foreground rounded border border-border mf-caption font-mono flex justify-between items-center">
                          <span className="font-semibold">
                            {pick(lang, guideCopy.crossSectionInflow)}
                          </span>
                          <span className="text-primary font-bold">20 Mesh</span>
                        </div>
                        <div className="p-3 bg-primary/10 rounded border border-primary/20 mf-caption font-mono flex justify-between items-center text-primary">
                          <span className="font-semibold">
                            {pick(lang, guideCopy.crossSectionCore)}
                          </span>
                          <span className="font-bold">
                            {pick(lang, guideCopy.crossSectionCoreFine100)}
                          </span>
                        </div>
                        <div className="p-3 bg-muted text-foreground rounded border border-border mf-caption font-mono flex justify-between items-center">
                          <span className="font-semibold">
                            {pick(lang, guideCopy.crossSectionSupport)}
                          </span>
                          <span className="text-primary font-bold">20 Mesh</span>
                        </div>
                      </div>
                      <p className="mf-caption text-muted-foreground pt-1">
                        {pick(lang, guideCopy.crossSectionFootnote3)}
                      </p>
                    </div>

                    <div className="lg:col-span-8 overflow-x-auto">
                      <Table className="min-w-[640px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[200px]">
                              {pick(lang, guideCopy.thLayerPosition)}
                            </TableHead>
                            <TableHead>
                              {pick(lang, guideCopy.thMeshSpec)}
                            </TableHead>
                            <TableHead>
                              {pick(lang, guideCopy.thWireAperture)}
                            </TableHead>
                            <TableHead>
                              {pick(lang, guideCopy.thFunction)}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {threeLayerExample.map((layer, index) => <TableRow key={index} data-index={index}>
                              <TableCell className="font-medium mf-caption sm:text-sm">
                                {pick(lang, layer.position)}
                              </TableCell>
                              <TableCell className="font-mono mf-caption font-semibold text-primary">
                                {layer.meshCount}
                              </TableCell>
                              <TableCell className="mf-caption text-muted-foreground font-mono">
                                {layer.wireDiameter} / {layer.aperture}
                              </TableCell>
                              <TableCell className="mf-caption">
                                {pick(lang, layer.purpose)}
                              </TableCell>
                            </TableRow>)}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>}

              {activePackType === "5-layer" && <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                    <div className="lg:col-span-4 bg-muted/40 p-5 rounded-lg border border-border flex flex-col gap-3">
                      <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                        <Cpu className="size-4" />
                        <span>{pick(lang, guideCopy.crossSectionTitle5)}</span>
                      </div>
                      <div className="space-y-1.5">
                        <div className="p-2 bg-muted text-foreground rounded border border-border mf-caption font-mono flex justify-between">
                          <span>{pick(lang, guideCopy.stepTopGuard)}</span>
                          <span className="font-bold">20 Mesh</span>
                        </div>
                        <div className="p-2 bg-muted text-foreground rounded border border-border mf-caption font-mono flex justify-between">
                          <span>{pick(lang, guideCopy.stepCoarseStep)}</span>
                          <span className="font-bold">60 Mesh</span>
                        </div>
                        <div className="p-2 bg-primary/15 rounded mf-caption font-mono flex justify-between text-primary font-bold">
                          <span>{pick(lang, guideCopy.stepPrecisionCore)}</span>
                          <span>150 Mesh</span>
                        </div>
                        <div className="p-2 bg-muted text-foreground rounded border border-border mf-caption font-mono flex justify-between">
                          <span>{pick(lang, guideCopy.stepCushionBed)}</span>
                          <span className="font-bold">60 Mesh</span>
                        </div>
                        <div className="p-2 bg-muted text-foreground rounded border border-border mf-caption font-mono flex justify-between">
                          <span>{pick(lang, guideCopy.stepRigidBase)}</span>
                          <span className="font-bold">20 Mesh Heavy</span>
                        </div>
                      </div>
                      <p className="mf-caption text-muted-foreground pt-1">
                        {pick(lang, guideCopy.crossSectionFootnote5)}
                      </p>
                    </div>

                    <div className="lg:col-span-8 overflow-x-auto">
                      <Table className="min-w-[640px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[200px]">
                              {pick(lang, guideCopy.thLayerPosition)}
                            </TableHead>
                            <TableHead>
                              {pick(lang, guideCopy.thMeshSpec)}
                            </TableHead>
                            <TableHead>
                              {pick(lang, guideCopy.thWireAperture)}
                            </TableHead>
                            <TableHead>
                              {pick(lang, guideCopy.thFunction)}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {fiveLayerExample.map((layer, index) => <TableRow key={index} data-index={index}>
                              <TableCell className="font-medium mf-caption sm:text-sm">
                                {pick(lang, layer.position)}
                              </TableCell>
                              <TableCell className="font-mono mf-caption font-semibold text-primary">
                                {layer.meshCount}
                              </TableCell>
                              <TableCell className="mf-caption text-muted-foreground font-mono">
                                {layer.wireDiameter} / {layer.aperture}
                              </TableCell>
                              <TableCell className="mf-caption">
                                {pick(lang, layer.purpose)}
                              </TableCell>
                            </TableRow>)}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </Reveal>

        {/* Process Selection Matrix Table */}
        <Reveal delay={120}>
          <div className="mb-16">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4">
              <div>
                <h3 className="mf-h3 text-foreground font-default">
                  {pick(lang, guideCopy.matrixTitle)}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {pick(lang, guideCopy.matrixDesc)}
                </p>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/ScreenPacksPage" className="inline-flex items-center gap-1.5">
                  <FileSpreadsheet className="size-4" />
                  <span>{pick(lang, guideCopy.viewAllSpecs)}</span>
                </Link>
              </Button>
            </div>

            <div className="border border-border rounded-lg bg-card overflow-x-auto">
              <Table className="min-w-[760px]">
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold text-foreground">
                      {pick(lang, guideCopy.thResinGrade)}
                    </TableHead>
                    <TableHead className="font-semibold text-foreground">
                      {pick(lang, guideCopy.thProcess)}
                    </TableHead>
                    <TableHead className="font-semibold text-foreground">
                      {pick(lang, guideCopy.thRecommendedPack)}
                    </TableHead>
                    <TableHead className="font-semibold text-foreground">
                      {pick(lang, guideCopy.thApertureRating)}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {standardPacks.map((pack, index) => <TableRow key={index} data-index={index}>
                      <TableCell className="font-semibold text-card-foreground">
                        {pick(lang, pack.resin)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {pick(lang, pack.typicalProcess)}
                      </TableCell>
                      <TableCell className="font-mono mf-caption font-medium text-primary">
                        {pack.recommendedPack}
                      </TableCell>
                      <TableCell className="text-sm">
                        {pick(lang, pack.targetFiltration)}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>
          </div>
        </Reveal>

        {/* Differential Pressure & Operational Safety Section */}
        <Reveal delay={120}>
          <div className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-7 space-y-6">
                <div>
                  <h3 className="mf-h3 text-foreground font-default">
                    {pick(lang, guideCopy.pressureDynamicsTitle)}
                  </h3>
                  <p className="text-muted-foreground text-sm mt-1">
                    {pick(lang, guideCopy.pressureDynamicsDesc)}
                  </p>
                </div>

                <div className="space-y-4">
                  {pressureFactors.map((item, index) => <div key={index} data-index={index} className="p-4 rounded-lg bg-card border border-border">
                      <div className="flex items-start gap-3">
                        <div className="size-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5 font-bold mf-caption">
                          {index + 1}
                        </div>
                        <div className="space-y-1 flex-1">
                          <h4 className="text-sm font-semibold text-foreground">
                            {pick(lang, item.title)}
                          </h4>
                          <p className="mf-caption text-muted-foreground leading-relaxed">
                            {pick(lang, item.description)}
                          </p>
                          <div className="pt-2 flex items-center gap-1.5 mf-caption font-medium text-primary">
                            <CircleCheck className="size-3.5" aria-hidden="true" />
                            <span>{pick(lang, item.impact)}</span>
                          </div>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>

              <div className="lg:col-span-5 space-y-6">
                <Alert className="border-border bg-card rounded-lg">
                  <ShieldAlert className="size-5 text-primary" />
                  <AlertTitle className="text-foreground font-semibold">
                    {pick(lang, guideCopy.alertTitle)}
                  </AlertTitle>
                  <AlertDescription className="mf-caption text-muted-foreground space-y-2 mt-2">
                    <p>{pick(lang, guideCopy.alertP1)}</p>
                    <p>{pick(lang, guideCopy.alertP2)}</p>
                  </AlertDescription>
                </Alert>

                <Card className="border-border bg-muted/40 rounded-lg">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Settings2 className="size-4 text-primary" />
                      <CardTitle className="text-sm font-bold text-foreground">
                        {pick(lang, guideCopy.packTypesCardTitle)}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 mf-caption text-muted-foreground">
                    <div className="flex items-start gap-2">
                      <div className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                      <p>
                        <strong className="text-foreground">
                          {pick(lang, guideCopy.packType1Title)}
                        </strong>{" "}
                        {pick(lang, guideCopy.packType1Desc)}
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                      <p>
                        <strong className="text-foreground">
                          {pick(lang, guideCopy.packType2Title)}
                        </strong>{" "}
                        {pick(lang, guideCopy.packType2Desc)}
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                      <p>
                        <strong className="text-foreground">
                          {pick(lang, guideCopy.packType3Title)}
                        </strong>{" "}
                        {pick(lang, guideCopy.packType3Desc)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </Reveal>

        <Separator className="my-10" />

        {/* Technical Assistance & CTA Strip */}
        <Reveal delay={60}>
          <div className="rounded-lg bg-muted border border-border p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-1 text-center md:text-left max-w-2xl">
              <h3 className="mf-h3 text-foreground font-default">
                {pick(lang, guideCopy.ctaBoxTitle)}
              </h3>
              <p className="text-sm text-muted-foreground">
                {pick(lang, guideCopy.ctaBoxDesc)}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
              <Button asChild size="lg">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, guideCopy.requestCustomQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/TechnicalResourcesPage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, guideCopy.technicalLibrary)}</span>
                </Link>
              </Button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}